import { useIntl } from "react-intl";
import { Occurrence } from "../../../models/occurrence";
import { getOccurrenceStepLabelByStepValue } from "../../../enums/occurrenceStep";
import { OccurrenceTypeLabelEnum } from "../../../enums/occurrenceType";
import { formatMilisecondsInDaysHoursMinutes } from "../../../utils/dateUtils";

interface Props {
    mainOccurrenceData: Occurrence;
    onClear?: null | VoidFunction;
}

const MainOccurenceCard = ({ mainOccurrenceData, onClear }: Props) => {
    const intl = useIntl();

    const getTimeInCurrentStep = (dtStart?: string | null) => {
        if (!dtStart) {
            return 'N/A';
        }

        const start = new Date(dtStart).getTime();
        const now = Date.now();

        if (Number.isNaN(start) || start > now) {
            return 'N/A';
        }

        return formatMilisecondsInDaysHoursMinutes(now - start);
    };

    const getTotalOccurrenceTime = (
        steps?: Occurrence['occurrenceSteps']
    ) => {
        if (!steps?.length) {
            return 'N/A';
        }

        const totalMilliseconds = steps.reduce((total, step) => {
            if (!step.dtStart) {
                return total;
            }

            const start = new Date(step.dtStart).getTime();

            const end = step.dtEnd
                ? new Date(step.dtEnd).getTime()
                : Date.now();

            if (
                Number.isNaN(start) ||
                Number.isNaN(end) ||
                end < start
            ) {
                return total;
            }

            return total + (end - start);
        }, 0);

        return formatMilisecondsInDaysHoursMinutes(totalMilliseconds);
    };

    const currentStep = mainOccurrenceData?.occurrenceSteps?.find((step) => !step.dtEnd);

    const currentStepTime = getTimeInCurrentStep(currentStep?.dtStart);

    const totalTime = getTotalOccurrenceTime(
        mainOccurrenceData?.occurrenceSteps
    );

    const showTotalTime = () => {
        if (mainOccurrenceData?.timeSpent) {
            return formatMilisecondsInDaysHoursMinutes(mainOccurrenceData.timeSpent * 60 * 1000);
        }

        if(totalTime) {
            return totalTime;
        }

        return 'N/A';
    };

    return (
        <div className="w-full h-28 mb-10 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-5 py-4 flex items-center justify-between shadow-sm">
            <div className="flex items-center w-full">
                <div className="flex items-center flex-1 mr-3 justify-center">
                    <p className="text-2lg font-bold">
                        {mainOccurrenceData?.protocolNumber || 'N/A'}
                    </p>
                </div>
                <div className="h-11 w-1 border-r border-gray-400" />
            </div>
            <div className="flex items-center w-full">
                <div className="flex justify-center items-center flex-1">
                    <div className="flex flex-col items-start">
                        <p className="text-2lg font-bold">
                            {intl.formatMessage({ id: "intl-msg:occurrence.card.type" })}
                        </p>
                        <p className="text-2lg">
                            {OccurrenceTypeLabelEnum[mainOccurrenceData?.occurrenceType as unknown as keyof typeof OccurrenceTypeLabelEnum] || 'N/A'}
                        </p>
                    </div>
                </div>
                <div className="h-11 w-1 border-r border-gray-400" />
            </div>
            <div className="flex items-center w-full">
                <div className="flex justify-center items-center flex-1">
                     <span className="inline-flex items-center justify-center rounded-full border border-[#E98A52] px-8 py-1 text-xl font-medium text-[#D9773F] bg-[#fff6f1]">
                         {currentStep?.stepId ? getOccurrenceStepLabelByStepValue(currentStep?.stepId) : 'Concluído'}
                    </span>
                </div>
                <div className="h-11 w-1 border-r border-gray-400" />
            </div>
            <div className="flex items-center w-full">
                <div className="flex justify-center items-center flex-1">
                    <div className="flex flex-col items-start">
                        <p className="text-2lg font-bold">
                            {intl.formatMessage({ id: "intl-msg:occurrence.card.currentStep" })}
                        </p>
                        <p className="text-2lg">
                            {currentStep ? currentStepTime : '-'}
                        </p>
                    </div>
                </div>
                <div className="h-11 w-1 border-r border-gray-400" />
            </div>
            <div className="flex items-center w-full">
                <div className="flex justify-center items-center flex-1">
                    <div className="flex flex-col items-start">
                        <p className="text-2lg font-bold">
                            {intl.formatMessage({ id: "intl-msg:occurrence.card.totalTime" })}
                        </p>
                        <p className="text-2lg">
                            {showTotalTime()}
                        </p>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-25">
                {onClear ? (
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            aria-label={intl.formatMessage({ id: "intl-msg:tas.occurrence.unlink-occurrence" })}
                            title={intl.formatMessage({ id: "intl-msg:tas.occurrence.unlink-occurrence" })}
                            className="btn btn-link padding-0 text-color-gray"
                            onClick={onClear}
                        >
                            <span className="rioglyph rioglyph-remove-sign margin-0" />
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export default MainOccurenceCard;